import { z } from 'zod';

import {
  type BettingSplitsESResult,
  type BettingSplitsMarket,
  type BettingSplitsResult,
} from '~/apps/product-plus/search-domain/pipelines/betting-splits/types';
import {
  type MatchupCentreESResult,
  type MatchupCentreMarket,
  type MatchupCentreResult,
} from '~/apps/product-plus/search-domain/pipelines/match-centre/types';
import { type ComputedAttributes } from '~/apps/product-plus/search-domain/pipelines/positive-ev/types';

import { formatInTimeZoneWithLocale } from './formatInTimeZone';

export const schemaTeam = z.object({
  badgeUrl: z.string().optional().nullable(),
  name: z.string(),
  nickName: z.string().optional(),
  shortName: z.string().optional(),
});

const schemaSubevent = z.object({
  categoryGroupId: z.number(),
  categoryGroupName: z.string(),
  categoryId: z.number(),
  categoryName: z.string(),
  eventDisplayName: z.string(),
  eventId: z.number(),
  subeventEndTime: z.string(),
  subeventId: z.number(),
  subeventName: z.string(),
  subeventStartTime: z.string(),
  teamData: z
    .object({
      away: schemaTeam,
      home: schemaTeam,
    })
    .optional()
    .nullable(),
  timestamp: z.string(),
});

export function extractSubevent(
  result: BettingSplitsESResult,
  { TZ, locale }: { TZ: string; locale: string },
): ComputedAttributes & BettingSplitsResult {
  const parsedSubevent = schemaSubevent.safeParse(result);

  if (!parsedSubevent.success) {
    throw new Error(`${JSON.stringify(parsedSubevent.error.flatten().fieldErrors)}`);
  }

  const {
    categoryGroupId,
    categoryGroupName,
    categoryId,
    categoryName,
    eventDisplayName,
    eventId,
    subeventEndTime,
    subeventId,
    subeventName,
    subeventStartTime,
    teamData,
    timestamp,
  } = parsedSubevent.data;

  return {
    betTypeId: [] as number[],
    categoryGroupId,
    categoryGroupName,
    categoryId,
    categoryName,
    eventDisplayName,
    eventId,
    markets: [] as BettingSplitsMarket[],
    subeventEndTime,
    subeventId,
    subeventName,
    subeventStartDate: formatInTimeZoneWithLocale(subeventStartTime, 'yyyy-MM-dd', TZ, locale),
    subeventStartTime,
    teamData: teamData as any,
    timestamp,
  };
}

export function extractMatchupCentreSubevent(
  result: MatchupCentreESResult,
  { TZ, locale }: { TZ: string; locale: string },
): ComputedAttributes & MatchupCentreResult {
  const parsedSubevent = schemaSubevent.safeParse(result);

  if (!parsedSubevent.success) {
    throw new Error(`${JSON.stringify(parsedSubevent.error.flatten().fieldErrors)}`);
  }

  const {
    categoryGroupId,
    categoryGroupName,
    categoryId,
    categoryName,
    eventDisplayName,
    eventId,
    subeventEndTime,
    subeventId,
    subeventName,
    subeventStartTime,
    teamData,
    timestamp,
  } = parsedSubevent.data;

  return {
    betTypeId: [] as number[],
    categoryGroupId,
    categoryGroupName,
    categoryId,
    categoryName,
    eventDisplayName,
    eventId,
    markets: [] as MatchupCentreMarket[],
    subeventEndTime,
    subeventId,
    subeventName,
    subeventStartDate: formatInTimeZoneWithLocale(subeventStartTime, 'yyyy-MM-dd', TZ, locale),
    subeventStartTime,
    teamData: teamData as any,
    timestamp,
  };
}
